/**
 * @file src/components/ui/ConfirmDialog.jsx
 * @description Generic confirmation dialog for destructive or important actions.
 *
 * Use for: delete, archive, send-irreversibly, etc.
 *
 * Pure presentational. The parent owns whatever async work happens after
 * confirmation and can pass `isLoading` to disable the buttons during it.
 *
 * Closes on:
 *  - Cancel button click
 *  - Backdrop click
 *  - Escape key press
 *
 * Example:
 *   <ConfirmDialog
 *     isOpen={isDeleting}
 *     title="Delete client?"
 *     message={`This will permanently delete ${client.name}. This cannot be undone.`}
 *     confirmLabel="Delete"
 *     variant="danger"
 *     isLoading={mutation.isPending}
 *     onConfirm={() => mutation.mutate(client.id)}
 *     onClose={() => setIsDeleting(false)}
 *   />
 */

import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";

const VARIANT_STYLES = {
  danger: {
    iconBg: "bg-red-100 dark:bg-red-900/20",
    iconColor: "text-red-600 dark:text-red-400",
    icon: "AlertTriangle",
    confirmClass: "bg-red-600 hover:bg-red-700 text-white",
  },
  warning: {
    iconBg: "bg-yellow-100 dark:bg-yellow-900/20",
    iconColor: "text-yellow-600 dark:text-yellow-400",
    icon: "AlertCircle",
    confirmClass: "bg-yellow-600 hover:bg-yellow-700 text-white",
  },
  info: {
    iconBg: "bg-blue-100 dark:bg-blue-900/20",
    iconColor: "text-blue-600 dark:text-blue-400",
    icon: "Info",
    confirmClass: "bg-primary hover:bg-primary/90 text-primary-foreground",
  },
};

const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = "danger",
  isLoading = false,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation("common");
  const styles = VARIANT_STYLES[variant] ?? VARIANT_STYLES.danger;
  const resolvedConfirmLabel = confirmLabel ?? t("actions.confirm");
  const resolvedCancelLabel = cancelLabel ?? t("actions.cancel");

  // Close on Escape (only when not loading)
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape" && !isLoading) onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
      onClick={() => {
        if (!isLoading) onClose();
      }}
      aria-modal="true"
      role="dialog"
      aria-label={title}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${styles.iconBg}`}
            >
              <Icon name={styles.icon} size={24} className={styles.iconColor} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {title}
              </h3>
              <p className="text-sm text-muted-foreground">{message}</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-muted/30 border-t border-border flex justify-end gap-3">
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={isLoading}
          >
            {resolvedCancelLabel}
          </Button>
          <Button
            size="md"
            onClick={onConfirm}
            disabled={isLoading}
            className={styles.confirmClass}
          >
            {isLoading ? (
              <>
                <Icon name="Loader" size={16} className="animate-spin" />
                <span className="ms-2">{t("actions.working")}</span>
              </>
            ) : (
              resolvedConfirmLabel
            )}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ConfirmDialog;
